import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Truck, Plus, Phone, Trash2, Shuffle, X, Star, Car, Wallet, MapPin } from "lucide-react";
import { toast } from "sonner";
import LiveDriversTracking from "./LiveDriversTracking";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const photoUrl = (p) => (p ? `${API}/files/${p}` : null);

const tierFromAvg = (avg, count) => {
  if (count < 3) return { name: "جديد", color: "#5C6E64", emoji: "🆕" };
  if (avg >= 4.5) return { name: "ذهبي", color: "#F5A623", emoji: "🥇" };
  if (avg >= 3.5) return { name: "فضي", color: "#9CA3AF", emoji: "🥈" };
  return { name: "برونزي", color: "#B87333", emoji: "🥉" };
};

const DriversTab = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showLiveTracking, setShowLiveTracking] = useState(false);
  const [form, setForm] = useState({
    full_name: "", phone: "", password: "", confirm_password: "",
    vehicle_type: "", vehicle_number: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [distributing, setDistributing] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/drivers`);
      setDrivers(data);
    } catch {
      toast.error("فشل تحميل السائقين");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || form.password.length < 6) {
      toast.error("جميع الحقول مطلوبة وكلمة السر 6 أحرف على الأقل");
      return;
    }
    if (form.password !== form.confirm_password) {
      toast.error("كلمتا السر غير متطابقتين");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/drivers`, {
        full_name: form.full_name,
        phone: form.phone,
        password: form.password,
        vehicle_type: form.vehicle_type,
        vehicle_number: form.vehicle_number,
      });
      toast.success("تم إضافة السائق");
      setForm({ full_name: "", phone: "", password: "", confirm_password: "", vehicle_type: "", vehicle_number: "" });
      setShowForm(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل إضافة السائق");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (d) => {
    if (!(await confirmDialog(`حذف السائق "${d.full_name}"؟`))) return;
    try {
      await axios.delete(`${API}/admin/drivers/${d.id}`);
      toast.success("تم حذف السائق");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الحذف");
    }
  };

  const handleDistribute = async () => {
    if (drivers.length === 0) { toast.error("أضف سائقاً أولاً"); return; }
    if (!(await confirmDialog(`توزيع كل الطلبات المعلقة على ${drivers.length} سائق؟`))) return;
    setDistributing(true);
    try {
      const { data } = await axios.post(`${API}/admin/orders/distribute`);
      toast.success(data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل التوزيع");
    } finally { setDistributing(false); }
  };

  return (
    <div data-testid="drivers-tab" className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="font-heading font-bold text-xl text-[#111814] flex items-center gap-2">
          <Truck className="w-6 h-6 text-[#4A90E2]" />
          السائقون
          <span className="text-sm text-[#5C6E64] font-normal">({drivers.length})</span>
        </h2>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowLiveTracking(true)}
            data-testid="open-live-tracking-btn"
            className="flex items-center gap-2 bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] hover:opacity-90 text-white font-bold px-4 py-2 rounded-full text-sm shadow-md">
            <MapPin className="w-4 h-4" />
            تتبّع مباشر
            <span className="w-2 h-2 rounded-full bg-[#22C55E] animate-pulse" />
          </button>
          <button onClick={handleDistribute} disabled={distributing}
            data-testid="distribute-btn"
            className="flex items-center gap-2 bg-[#F5A623] hover:bg-[#dc9320] text-white font-bold px-4 py-2 rounded-full text-sm disabled:opacity-50">
            <Shuffle className="w-4 h-4" />
            توزيع تلقائي
          </button>
          <button onClick={() => setShowForm(!showForm)} data-testid="add-driver-btn"
            className="flex items-center gap-2 bg-[#2D4A3E] hover:bg-[#1f3329] text-white font-bold px-4 py-2 rounded-full text-sm">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "إلغاء" : "إضافة سائق"}
          </button>
        </div>
      </div>

      {showForm && (
        <motion.form
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          onSubmit={handleCreate}
          className="bg-white border border-[#E4EBE6] rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          <input type="text" placeholder="اسم السائق ثلاثي *"
            value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            data-testid="driver-name-input" required
            className="px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E]" />
          <input type="tel" placeholder="رقم السائق *"
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, "") })}
            data-testid="driver-phone-input" required
            className="px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E]" />
          <input type="text" placeholder="نوع وسيلة النقل (سيارة/دراجة...)"
            value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
            data-testid="driver-vehicle-type"
            className="px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E]" />
          <input type="text" placeholder="رقم العجلة"
            value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value })}
            data-testid="driver-vehicle-number"
            className="px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E]" />
          <input type="text" placeholder="كلمة السر (6 أحرف+) *"
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="driver-password-input" required minLength={6}
            className="px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E]" />
          <input type="text" placeholder="تأكيد كلمة السر *"
            value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
            data-testid="driver-confirm-password" required minLength={6}
            className="px-4 py-3 rounded-2xl border border-[#E4EBE6] focus:outline-none focus:ring-2 focus:ring-[#2D4A3E]" />
          <button type="submit" disabled={submitting} data-testid="driver-submit-btn"
            className="sm:col-span-2 bg-[#7ED321] hover:bg-[#6cb91d] text-white font-bold px-4 py-3 rounded-2xl disabled:opacity-50">
            {submitting ? "جاري الإضافة..." : "✓ إضافة السائق"}
          </button>
        </motion.form>
      )}

      {loading ? (
        <p className="text-center text-[#5C6E64] py-8">جاري التحميل...</p>
      ) : drivers.length === 0 ? (
        <div className="bg-white border border-[#E4EBE6] rounded-2xl py-12 text-center">
          <Truck className="w-16 h-16 text-[#E4EBE6] mx-auto mb-3" />
          <p className="text-[#5C6E64]">لا يوجد سائقون</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="drivers-list">
          {drivers.map((d, idx) => {
            const tier = tierFromAvg(d.avg_rating, d.rating_count);
            return (
              <motion.div key={d.id}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}
                data-testid={`driver-${d.id}`}
                className="bg-white border border-[#E4EBE6] rounded-2xl p-4 space-y-3">
                <div className="flex justify-between items-start gap-2">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {photoUrl(d.photo_path) ? (
                      <img
                        src={photoUrl(d.photo_path)}
                        alt={d.full_name}
                        data-testid={`driver-photo-${d.id}`}
                        className="w-12 h-12 rounded-full object-cover border-2 border-[#3B82F6] flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#F0F9FF] border-2 border-[#3B82F6] flex items-center justify-center flex-shrink-0">
                        <Truck className="w-5 h-5 text-[#3B82F6]" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-[#111814]">{d.full_name}</p>
                      <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: `${tier.color}25`, color: tier.color }}>
                        {tier.emoji} {tier.name}
                      </span>
                    </div>
                    <a href={`tel:${d.phone}`} className="text-xs text-[#E85D45] flex items-center gap-1 mt-1 hover:underline">
                      <Phone className="w-3 h-3" />{d.phone}
                    </a>
                    {(d.vehicle_type || d.vehicle_number) && (
                      <p className="text-xs text-[#5C6E64] mt-1 flex items-center gap-2">
                        <Car className="w-3 h-3 text-[#4A90E2]" />
                        {d.vehicle_type} {d.vehicle_number && `· ${d.vehicle_number}`}
                      </p>
                    )}
                    </div>
                  </div>
                  <button onClick={() => handleDelete(d)} data-testid={`delete-driver-${d.id}`}
                    className="bg-[#E85D45] hover:bg-[#c14a36] text-white text-xs font-bold px-2.5 py-1.5 rounded-full">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-[#F3F6F4] rounded-xl p-2">
                    <p className="text-[10px] text-[#5C6E64]">اليوم</p>
                    <p className="font-bold text-sm">{d.today_delivered}</p>
                  </div>
                  <div className="bg-[#F3F6F4] rounded-xl p-2">
                    <p className="text-[10px] text-[#5C6E64]">الشهر</p>
                    <p className="font-bold text-sm">{d.month_delivered}</p>
                  </div>
                  <div className="bg-[#F3F6F4] rounded-xl p-2">
                    <p className="text-[10px] text-[#5C6E64]">⭐ تقييم</p>
                    <p className="font-bold text-sm">{d.avg_rating || "—"}</p>
                  </div>
                </div>

                {/* Bonus balance REMOVED (Feb 17, 2026) — admin disabled
                    the 30 IQD/carton commission. */}

                {d.cash_collected > 0 && (
                  <div className="bg-[#FFE8E5] border border-[#E85D45]/30 rounded-xl px-3 py-2 text-sm flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-[#E85D45]" />
                    <span className="text-xs text-[#5C6E64]">عنده مبلغ غير مسلَّم:</span>
                    <span className="font-bold text-[#E85D45] mr-auto">{fmt(d.cash_collected)} د.ع</span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}

      {showLiveTracking && <LiveDriversTracking onClose={() => setShowLiveTracking(false)} />}
    </div>
  );
};

export default DriversTab;
